import Axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// API Base URL - uses environment variable in production, empty string in development (uses proxy)
//const API_BASE_URL = import.meta.env.PROD ? (import.meta.env.VITE_API_BASE_URL || '') : '';
const API_BASE_URL = '';

const isSsoSession = () => localStorage.getItem("auth_mode") === "sso";
let isRefreshing = false;
let refreshWaiters = [];

// API path constants
const bp = '/bp';


const refresh_access_token = async () => {
  if (!isSsoSession()) return false;
  const refreshToken = localStorage.getItem('refresh_token') || '';
  if (!refreshToken) return false;
  try {
    const res = await fetch(getFullUrl('/bmr/api/auth/oidc/refresh'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const payload = await res.json();
    if (!res.ok || payload?.status !== 'success' || !payload?.data?.access_token) {
      return false;
    }
    localStorage.setItem('Token', payload.data.access_token);
    if (payload?.data?.refresh_token) {
      localStorage.setItem('refresh_token', payload.data.refresh_token);
    }
    return true;
  } catch {
    return false;
  }
};

const queueRefreshWaiter = () => new Promise((resolve) => refreshWaiters.push(resolve));
const flushRefreshWaiters = (ok) => {
  const waiters = [...refreshWaiters];
  refreshWaiters = [];
  waiters.forEach((resolve) => resolve(ok));
};

Axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    if (!isSsoSession()) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      const ok = await queueRefreshWaiter();
      if (!ok) return Promise.reject(error);
      original._retry = true;
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${localStorage.getItem('Token') || ''}` };
      return Axios(original);
    }

    isRefreshing = true;
    const ok = await refresh_access_token();
    isRefreshing = false;
    flushRefreshWaiters(ok);

    if (!ok) return Promise.reject(error);
    original._retry = true;
    original.headers = { ...(original.headers || {}), Authorization: `Bearer ${localStorage.getItem('Token') || ''}` };
    return Axios(original);
  }
);

// Helper function to get active plant ID from localStorage
const getActivePlantId = () => {
  try {
    const activePlant = localStorage.getItem('active-plant');
    if (activePlant && activePlant !== 'null' && activePlant !== 'undefined') {
      const plant = JSON.parse(activePlant);
      return plant?.id || null;
    }
  } catch (error) {
    console.error('Error parsing active plant from localStorage:', error);
  }
  return null;
};

// Helper function to construct full URL
const getFullUrl = (url) => {
  // If URL already starts with http/https, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  console.log(`${API_BASE_URL}${url}`);
  // Otherwise, prepend base URL
  return `${API_BASE_URL}${url}`;
};

/**
 * Centralized HTTP-status handling for fetch-based endpoints in this file.
 *
 * Use it like:
 *   const response = await fetch(...);
 *   await handleResponse(response, success, { onError });
 *
 * Behaviour:
 *   • 2xx  → parses body and invokes success(data)
 *   • 400  → shows a toast with server message (or "Invalid request") and calls onError
 *   • 401  → shows "Session expired" toast and calls onError (callers can navigate to /login)
 *   • 403  → shows "You are not authorized" toast and calls onError
 *   • 404  → shows "Not found" toast and calls onError
 *   • 409  → shows "Conflict" toast (or server message) and calls onError
 *   • 5xx  → shows "Server error" toast and calls onError
 *   • Other → shows generic toast and calls onError
 *
 * `onError` receives `{ status, data }` so the caller can react (e.g. clear a form,
 * redirect on 401, etc.). If onError is not provided, the helper logs and resolves.
 *
 * `silent: true` suppresses toasts (useful for polling endpoints).
 */
export const handleResponse = async (response, success, options = {}) => {
  const { onError, silent = false } = options;
  let data = {};
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : {};
  } catch (_e) {
    data = {};
  }

  const { status } = response;
  if (response.ok) {
    if (typeof success === 'function') success(data);
    return { ok: true, status, data };
  }

  const serverMessage = data?.message || data?.detail || data?.error;
  let userMessage;
  switch (status) {
    case 400:
      userMessage = serverMessage || 'Invalid request';
      break;
    case 401:
      userMessage = 'Session expired. Please log in again.';
      break;
    case 403:
      userMessage = 'You are not authorized to perform this action.';
      break;
    case 404:
      userMessage = serverMessage || 'Resource not found';
      break;
    case 409:
      userMessage = serverMessage || 'Conflict — the resource is in an inconsistent state.';
      break;
    default:
      if (status >= 500) {
        userMessage = 'Server error. Please try again later.';
      } else {
        userMessage = serverMessage || `Request failed (${status})`;
      }
  }

  if (!silent) toast.error(userMessage);
  if (typeof onError === 'function') onError({ status, data });
  else console.warn(`[api] ${status} ${response.url}:`, data);

  return { ok: false, status, data };
};

export const get_function = (url, success, params = {}, signal = null) => {
  const token = localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to params if it exists
  const requestParams = plantId ? { ...params, plant_id: plantId } : params;

  Axios.get(getFullUrl(url), {
    params: requestParams,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      if (res?.status === 200) {
        success(res);
      } else {
        console.log('failed', res);
      }
    })
    .catch((error) => {
      // Handle unauthorized (session expired)
      if (error?.response?.status === 401) {
        const msg = error?.response?.data?.detail || error?.response?.data?.message || 'Session expired. Please login again.';
        try { toast.error(String(msg)); } catch (e) {}
        localStorage.clear();
        setTimeout(() => { window.location.href = '/login'; }, 300);
      }
      let res = {
        data: {
          data: [],
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch data',
        },
      };
      success(res);
      console.error('There was an error!', error);
    });
};

export const post_function = async (url, success, data, isJson = true) => {
  const token = localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (isJson) {
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
  }

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  try {
    const response = await Axios.post(getFullUrl(url), requestData, { headers });
    success(response);
  } catch (error) {
    let res = {
      data: {
        status: 'failed',
        message: error?.response?.data?.message || 'Failed to add data',
      },
    };
    success(res);
  }
};

export const update_function = async (url, success, data, isJson = true) => {
  const token = localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (isJson) {
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
  }

  // Prepend the `/bp` API prefix unless the caller already provided it,
  // matching post_api_function/get_api_function so bare paths like
  // `/location/...` resolve to the backend (which is mounted under /bp/).
  const finalUrl = url?.startsWith('/bp/') ? url : `${bp}${url ?? ''}`;

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  try {
    const response = await Axios.put(getFullUrl(finalUrl), requestData, { headers });
    success(response);
  } catch (error) {
    success(error.response || error);
  }
};

export const delete_function = async (url, success) => {
  const token = localStorage.getItem('Token');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  // Prepend the `/bp` API prefix unless the caller already provided it,
  // matching post_api_function/get_api_function so bare paths like
  // `/location/...` resolve to the backend (which is mounted under /bp/).
  const finalUrl = url?.startsWith('/bp/') ? url : `${bp}${url ?? ''}`;
  try {
    const response = await Axios.delete(getFullUrl(finalUrl), { headers });
    success(response);
  } catch (error) {
    success(error.response || error);
  }
};

export const login_api = async (username, password, success) => {
  const response = await fetch(getFullUrl(`${bp}/token/`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: username,
      password: password,
    }),
  });
  await handleResponse(response, success);
};



export const get_oidc_start_api = async (success) => {
  try {
    const res = await fetch(getFullUrl(`${bp}/api/auth/oidc/start`), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const data = await res.json();
    success(data);
  } catch (err) {
    success({ status: 'failed', message: err.message || 'Failed to fetch SSO configuration' });
  }
};

export const get_oidc_logout_url = () => getFullUrl(`${bp}/api/auth/oidc/logout`);
export const user_login_log = async (loginData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof loginData === 'object' && !Array.isArray(loginData)
      ? { ...loginData, plant_id: plantId }
      : loginData;

  const response = await fetch(getFullUrl(`${bp}/user_login_log`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const user_logout_log = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/user_logout_log`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_api_function = async (url, success, params, signal) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const finalUrl = url?.startsWith('/bp/') ? url : `${bp}${url ?? ''}`;
  // Add plant_id to params if it exists
  const requestParams = plantId ? { ...params, plant_id: plantId } : params;

  Axios.get(finalUrl, {
    params: requestParams,
    signal: signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const post_api_function = async (url, success, data) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const finalUrl = url?.startsWith('/bp/') ? url : `${bp}${url ?? ''}`;
  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(finalUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const forgot_password_API = async (email, success) => {
  const response = await fetch(getFullUrl(`${bp}/forgot_password`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: email,
    }),
  });
  await handleResponse(response, success);
};

export const set_password_API = async (set_password_data, success) => {
  const response = await fetch(getFullUrl(`${bp}/set_password`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(set_password_data),
  });
  await handleResponse(response, success);
};

export const get_user_details = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/userprofile`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then((res) => {
      success(res.data);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_all_ticketDetails = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/create_ticket`, {
    params: {
      search_item: search_item,
      page_number: page_number,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_user_all_ticketDetails = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_all_ticket`, {
    params: {
      search_item: search_item,
      page_number: page_number,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ticket_approval = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/ticket_approval`, {
    params: {
      search_item: search_item,
      page_number: page_number,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const main_dashboard_analysis = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/dashboard_analysis`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const count_analysis_ticket_user = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/analysis_ticket_user`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const count_analysis_ticket_manager = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/analysis_for_manager`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_issue_types = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_issue_type_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ticket_details = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_ticket_details`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      search: search_item || '',
      page: page_number || 1,
      page_size: 10,
      ...(plantId ? { plant_id: plantId } : {}),
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_locations = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_locations`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_departments = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/departdata`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_shift = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_employee_shift`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_prioritys = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_priority_level`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_priority_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_priority_status`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

// export const add_Ticket = async (ticketFormData, success) => {

//   const response = await fetch(getFullUrl(`${bp}/pm/create_ticket`), {
//     method: "POST",
//     headers: {
//       Authorization: `Bearer ${token}`,
//     },
//     body: ticketFormData,
//   });
//   const text = await response.text();
//   if (response.status === 200) {
//     console.log("success", JSON.parse(text));
//     success(JSON.parse(text));
//   } else {
//     console.log("failed", text);
//     Object.entries(JSON.parse(text)).forEach(([key, value]) => {});
//   }
// };

export const add_Ticket = async (ticketFormData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && ticketFormData instanceof FormData) {
    ticketFormData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/add_ticket_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: ticketFormData,
  });
  await handleResponse(response, success);
};

export const get_employee_working_status = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/employee_working_status`, {
    params: { search_item: search_item, page_number: page_number },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_employee_work_status = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_employee_work_status`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const add_employee_working_status = async (statusFormData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && statusFormData instanceof FormData) {
    statusFormData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/pm/employee_working_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: statusFormData,
  });
  await handleResponse(response, success);
};

export const update_ticket_status = async (ticket_id_to_be_updated, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId &&
    typeof ticket_id_to_be_updated === 'object' &&
    !Array.isArray(ticket_id_to_be_updated)
      ? { ...ticket_id_to_be_updated, plant_id: plantId }
      : ticket_id_to_be_updated;

  const response = await fetch(getFullUrl(`${bp}/pm/update_ticket_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      // Accept: "application/json",
      // "Content-Type": "application/json",
    },
    body: requestData,
  });
  await handleResponse(response, success);
};

export const update_workflow_engin = async (workflow_tobe_updated, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof workflow_tobe_updated === 'object' && !Array.isArray(workflow_tobe_updated)
      ? { ...workflow_tobe_updated, plant_id: plantId }
      : workflow_tobe_updated;

  const response = await fetch(getFullUrl(`${bp}/pm/workflow_engin`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: requestData,
  });
  await handleResponse(response, success);
};

export const get_employees_for_assign = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_employee_assign`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_department_approval_data = async (success, search_item, page_number) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/department_approval_data`, {
    params: { search_item: search_item, page_number: page_number },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const add_issue_type = async (issueType, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof issueType === 'object' && !Array.isArray(issueType)
      ? { ...issueType, plant_id: plantId }
      : issueType;

  const response = await fetch(getFullUrl(`${bp}/pm/add_issue_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: requestData,
  });
  await handleResponse(response, success);
};

export const get_roles = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_role`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_roles_config = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_role_config`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_designations = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_designation`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_employee = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_employee_user`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_machine_shift = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_machine_shift`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_instruction_status = async (success, ticket_id) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_instruction_status`, {
    params: { ticket_id: ticket_id },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const reopen_ticket = async (reopen_formdata, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof reopen_formdata === 'object' && !Array.isArray(reopen_formdata)
      ? { ...reopen_formdata, plant_id: plantId }
      : reopen_formdata;

  const response = await fetch(getFullUrl(`${bp}/pm/ticket_reopen`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: requestData,
  });
  await handleResponse(response, success);
};
export const return_ticket = async (return_formdata, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof return_formdata === 'object' && !Array.isArray(return_formdata)
      ? { ...return_formdata, plant_id: plantId }
      : return_formdata;

  const response = await fetch(getFullUrl(`${bp}/pm/return_ticket`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: requestData,
  });
  await handleResponse(response, success);
};

export const ticket_workflow_creation = async (ticketFlow, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof ticketFlow === 'object' && !Array.isArray(ticketFlow)
      ? { ...ticketFlow, plant_id: plantId }
      : ticketFlow;

  const response = await fetch(getFullUrl(`${bp}/pm/ticket_workflow_creation`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ticket_flow: ticketFlow,
    }),
  });
  await handleResponse(response, success);
};

export const get_ticket_workflow_creation = async (success, issue_type_id) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/ticket_workflow_creation`, {
    params: { issue_type_id: issue_type_id },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    success(res.data);
  });
};

export const issue_type_dept_config = async (issueTypeDeptConfig, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof issueTypeDeptConfig === 'object' && !Array.isArray(issueTypeDeptConfig)
      ? { ...issueTypeDeptConfig, plant_id: plantId }
      : issueTypeDeptConfig;

  const response = await fetch(getFullUrl(`${bp}/pm/add_issue_depart`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// export const get_issue_type_dept = async (success) => {
//   const token = await localStorage.getItem("Token");

//   Axios.get("/pm/add_issue_depart", {
//     headers: {
//       Authorization: `Bearer ${token}`,
//     },
//   }).then((res) => {
//     success(res.data);
//   });
// };

export const get_issue_type_dept = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/departdata`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    success(res.data);
  });
};

export const add_employee_user = async (user_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof user_data === 'object' && !Array.isArray(user_data)
      ? { ...user_data, plant_id: plantId }
      : user_data;

  const response = await fetch(getFullUrl(`${bp}/bp_add_employee_user`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_employee = async (employee_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof employee_id === 'object' && !Array.isArray(employee_id)
      ? { ...employee_id, plant_id: plantId }
      : employee_id;

  const response = await fetch(getFullUrl(`${bp}/bp_delete_employee`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ employee_id }),
  });
  await handleResponse(response, success);
};

export const get_employee_user_list = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // const params = department_id
  //   ? { department_id: department_id }
  //   : designation_id
  //   ? { designation_id: designation_id }
  //   : {};

  Axios.get(`${bp}/bp_get_employee_user`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    success(res.data);
  });
};

export const bp_get_access = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/bp_get_access`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    success(res.data);
  });
};

export const update_access_list_admin = async (access_list_to_be_updated, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId &&
    typeof access_list_to_be_updated === 'object' &&
    !Array.isArray(access_list_to_be_updated)
      ? { ...access_list_to_be_updated, plant_id: plantId }
      : access_list_to_be_updated;

  const response = await fetch(getFullUrl(`${bp}/bp_add_access`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: access_list_to_be_updated }),
  });
  await handleResponse(response, success);
};

export const get_employee_access_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_employee_access_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    success(res.data.data);
  });
};

export const add_department = async (department, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof department === 'object' && !Array.isArray(department)
      ? { ...department, plant_id: plantId }
      : department;

  const response = await fetch(getFullUrl(`${bp}/departdata`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_designation = async (department, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof department === 'object' && !Array.isArray(department)
      ? { ...department, plant_id: plantId }
      : department;

  const response = await fetch(getFullUrl(`${bp}/bp_add_designation`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_location = async (location, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof location === 'object' && !Array.isArray(location)
      ? { ...location, plant_id: plantId }
      : location;

  const response = await fetch(getFullUrl(`${bp}/pm/location_config`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_role = async (role, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof role === 'object' && !Array.isArray(role)
      ? { ...role, plant_id: plantId }
      : role;

  const response = await fetch(getFullUrl(`${bp}/bp_add_role`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_role = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_delete_role`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_role_group = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/bp_get_role_group`), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  await handleResponse(response, success);
};

export const add_role_group = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_add_role_group`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_role_group = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_delete_role_group`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_employee_role_config1 = async (success, employee_id) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/add_employee_role_config`, {
    params: { employee_id: employee_id },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const add_location_group = async (group_name, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof group_name === 'object' && !Array.isArray(group_name)
      ? { ...group_name, plant_id: plantId }
      : group_name;

  const response = await fetch(getFullUrl(`${bp}/pm/add_group`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      group: group_name,
    }),
  });
  await handleResponse(response, success);
};

export const get_location_groups = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/pm/get_group`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const genarate_pdf_for_ticket = async (success, ticket_id) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof success === 'object' && !Array.isArray(success)
      ? { ...success, plant_id: plantId }
      : success;

  const response = await fetch(getFullUrl(`${bp}/genarate_pdf_for_ticket`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ticket_id: ticket_id,
    }),
  });
  await handleResponse(response, success);
};

//##############Batch Processing Api's ###################

export const bp_machine_data = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_machine_data`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_machine_by_machine_typ = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_machine_by_machine_type`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_testing_type = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_testing_type`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_details = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_details`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_material_data = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_material`, {
    params: {
      ...param,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_material_type_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_material_type`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_material_category = async (success, failure) => {
  try {
    const token = await localStorage.getItem('Token');
    const plantId = getActivePlantId();
    const res = await Axios.get(`${bp}/get_material_category`, {
      params: plantId ? { plant_id: plantId } : {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res?.status === 200) {
      success?.(res);
    } else {
      toast.warning('No data found');
    }
  } catch (error) {
    console.warn('Material categories fetch failed:', error?.response?.status || error?.message);
    failure?.('Unable to fetch material categories. Please try again later.');
  }
};

export const add_material_data = async (materialData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof materialData === 'object' && !Array.isArray(materialData)
      ? { ...materialData, plant_id: plantId }
      : materialData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_material`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_material_category = async (materialData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof materialData === 'object' && !Array.isArray(materialData)
      ? { ...materialData, plant_id: plantId }
      : materialData;

  const response = await fetch(getFullUrl(`${bp}/add_material_category`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_update_starttime_for_batch_number = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_update_starttime_for_batch_number`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_simulation_data = async (data, success) => {
  const response = await fetch(getFullUrl(`${bp}/save_simulation_data`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};
export const update_recipe_value = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/update_recipe_value`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_batch_ingredients = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_save_batch_ingredients`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_batch_product_inventory = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_save_batch_product_inventory`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_sample_details = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_save_sample_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_batch_parameters = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_save_batch_parameters`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_batch_configuration = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/bp_batch_configuration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_batch_configuration_pharma = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/batch_configuration_pharma`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_batch_details_by_process_id = async (success, params, signal) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_details_by_process_id`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((e) => {
      console.log(e);
    });
};

export const bp_parameters_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_parameters`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_report_view = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_report_view`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_recipe_meta_data = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_recipe_with_batch`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_schedule_recipe_list = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_schedule_recipe_list`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_number_by_recipe = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_number_by_recipe`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_numbers = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_numbers`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_list = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_recipe_list`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_process_active = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_process_active`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_sample_detailss = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_sample_detailss`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_qc_sample_queue = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_qc_sample_queue`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

// Completed / rejected QC samples with the values that were entered for each parameter.
export const bp_get_qc_sample_history = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_qc_sample_history`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_sampling_details_by_sample = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_sampling_details_by_sample`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_by_product_id = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_recipe_by_product_id`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_process_operation_by_recipe = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_process_operation_by_recipe`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_process_schedule = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_process_schedule`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_supplier_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_supplier_data`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_inventory_data = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_material_inventory`, {
    params: {
      ...param,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_product_data = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_product_data`, {
    params: {
      ...param,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_product_by_id = async (product_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_product_by_id`, {
    params: {
      product_id,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_product_list_both = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_product_list_both`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_units_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_unit`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_location_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_location`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_warehouse_areas_dropdown = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_warehouse_areas_dropdown`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('Error fetching warehouse areas:', error));
};

export const get_machine_status_list = async (success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_machine_status_list`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('Error fetching machine statuses:', error));
};

export const bp_get_approvel_status_for_batch = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_approvel_status_for_batch`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_machine_category_with_groups = async (success, process_id) => {
  const token = await localStorage.getItem('Token');
  const params = process_id ? { process_id } : {};
  Axios.get(`${bp}/get_machine_category_with_groups`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

// Unified role-based dashboard KPI feed.
// Usage:
//   get_dashboard_kpis('OPERATOR', (res) => { ... res.data.data ... });
export const get_dashboard_kpis = async (role, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/dashboard/kpis`, {
    params: {
      role,
      ...(plantId && { plant_id: plantId }),
    },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Dashboard KPIs fetch failed:', error);
      success({ data: { status: 'failed', data: {} } });
    });
};

// Batch 360 — list of batches for the selector.
export const get_batch_list_360 = async (search, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/dashboard/batch-list`, {
    params: { ...(search && { search }), ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Batch list fetch failed:', error);
      success({ data: { status: 'failed', data: [] } });
    });
};

// Batch 360 — full single-batch detail (overview, operations, equipment,
// cleaning, line clearance, deviations, sampling, e-sign).
export const get_batch_360 = async (batchNumber, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/dashboard/batch-360`, {
    params: { batch_number: batchNumber },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Batch 360 fetch failed:', error);
      success({ data: { status: 'failed', data: {} } });
    });
};

// Full batch audit trail — summary counts, batch/recipe config, operations,
// machine occupancy + cleaning, change history, e-sign, handovers, deviations,
// and a unified cross-source timeline. Used by the Batch Traceability page.
export const get_batch_full_audit_trail = async (batchNumber, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/batch_full_audit_trail`, {
    params: { batch_number: batchNumber, _ts: Date.now() },
    headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Batch full audit trail fetch failed:', error);
      success({ data: { status: 'failed' } });
    });
};

// Unified machine tracking logs for a batch — per-machine occupancy windows +
// events, cleaning records with approvals, and a flat timeline. Optional
// machine_id restricts to one machine. Response data is at the TOP LEVEL of res.data.
export const get_machine_occupancy_log = async ({ machineId, fromDate, toDate }, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/machine_occupancy_log`, {
    params: {
      machine_id: machineId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
      _ts: Date.now(),
    },
    headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Machine occupancy log fetch failed:', error);
      success({ data: { status: 'failed' } });
    });
};

export const get_batch_machine_tracking_logs = async (batchNumber, machineId, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/batch_machine_tracking_logs`, {
    params: {
      batch_number: batchNumber,
      ...(machineId ? { machine_id: machineId } : {}),
      _ts: Date.now(),
    },
    headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Batch machine tracking logs fetch failed:', error);
      success({ data: { status: 'failed' } });
    });
};

// QA dashboard — aggregated approvals (recipe/process/batch/release),
// deviations, e-sign activity, charts data, and pending queues.
export const get_qa_dashboard = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/dashboard/qa`, {
    params: { ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('QA dashboard fetch failed:', error);
      success({ data: { status: 'failed', data: {} } });
    });
};

// Per-batch workspace detail — workflow stages + alerts + timeline.
// Used by the Selected Batch Workspace panel on the Admin Cockpit dashboard.
export const get_batch_workspace = async (batchId, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/dashboard/batch-workspace`, {
    params: { batch_id: batchId },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Batch workspace fetch failed:', error);
      success({ data: { status: 'failed', data: {} } });
    });
};

// Returns distinct From-Machine categories used by ingredients of the given operation.
// Used by the Carry Over → Previous Operation flow in Ingredients form.
export const get_ingredient_from_machine_categories_by_op = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ingredient_from_machine_categories_by_op`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Error fetching ingredient from-machine categories:', error);
      success({ data: { status: 'failed', data: [] } });
    });
};

// Fetch a single operation's saved Machine Setup (category/group/machine_ids)
// for the Machine Setup "Carry Over" feature.
export const get_operation_machine_setup = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_operation_machine_setup`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Error fetching operation machine setup:', error);
      success({ data: { status: 'failed', message: 'Failed to fetch machine setup' } });
    });
};

export const bp_machine_cat_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_machine_cat_data`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_recipe_MetaData_ById = async (recipeId, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_recipe_meta_data`, {
    params: {
      ...(recipeId && { recipe_md_id: recipeId }),
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_process_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_process_data`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_Ingredients = async (recipe_md_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_recipe_ingredients_data`, {
    params: {
      recipe_md_id: recipe_md_id,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const bp_add_product_data = async (productData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof productData === 'object' && !Array.isArray(productData)
      ? { ...productData, plant_id: plantId }
      : productData;

  const response = await fetch(getFullUrl(`${bp}/save_product_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const edit_product_data = async (productData, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/edit_product_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(productData),
  });
  await handleResponse(response, success);
};

export const save_grafana_url = async (urlData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof urlData === 'object' && !Array.isArray(urlData)
      ? { ...urlData, plant_id: plantId }
      : urlData;

  const response = await fetch(getFullUrl(`${bp}/save_grafana_url`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_device_details = async (devicedata, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof devicedata === 'object' && !Array.isArray(devicedata)
      ? { ...devicedata, plant_id: plantId }
      : devicedata;

  const response = await fetch(getFullUrl(`${bp}/save_device_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const sensor_configuration = async (devicedata, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof devicedata === 'object' && !Array.isArray(devicedata)
      ? { ...devicedata, plant_id: plantId }
      : devicedata;

  const response = await fetch(getFullUrl(`${bp}/sensor_configuration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_sensor_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_sensor_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
// export const get_device_details = async (success) => {
//   const token = await localStorage.getItem('Token');
//   const plantId = getActivePlantId();
//
//   Axios.get(`${bp}/get_device_details`, {
//     params: plantId ? { plant_id: plantId } : {},
//     headers: {
//       Authorization: `Bearer ${token}`,
//     },
//   })
//     .then((res) => {
//       success(res);
//     })
//     .catch((error) => {
//       console.error('There was an error!', error);
//     });
// };
export const bp_get_all_Ingredients = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_recipe_ingredients_data`, {
    params: {
      ...param,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_weighing_machine_by_category = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_weighing_machine_by_category`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_add_parameter_data = async (parameterData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof parameterData === 'object' && !Array.isArray(parameterData)
      ? { ...parameterData, plant_id: plantId }
      : parameterData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_parameters`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const delete_operation_details = async (configData, success) => {
  try {
    const token = await localStorage.getItem('Token');
    const plantId = getActivePlantId();

    // Add plant_id to data if it exists and data is an object
    const requestData =
      plantId && typeof configData === 'object' && !Array.isArray(configData)
        ? { ...configData, plant_id: plantId }
        : configData;

    const response = await fetch(getFullUrl(`${bp}/delete_operation_details`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    await handleResponse(response, success);
  } catch (error) {
    console.log('failed', error);
  }
};

export const bp_update_approval_status_for_recipe = async (approval_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof approval_data === 'object' && !Array.isArray(approval_data)
      ? { ...approval_data, plant_id: plantId }
      : approval_data;

  const response = await fetch(getFullUrl(`${bp}/bp_update_approval_status_for_recipe`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const update_batch_recipe_approval_status = async (approval_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof approval_data === 'object' && !Array.isArray(approval_data)
      ? { ...approval_data, plant_id: plantId }
      : approval_data;

  const response = await fetch(getFullUrl(`${bp}/update_batch_recipe_approval_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const edit_recipe_machine = async (approval_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof approval_data === 'object' && !Array.isArray(approval_data)
      ? { ...approval_data, plant_id: plantId }
      : approval_data;

  const response = await fetch(getFullUrl(`${bp}/edit_recipe_machine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_machine_cat_data = async (catData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof catData === 'object' && !Array.isArray(catData)
      ? { ...catData, plant_id: plantId }
      : catData;

  const response = await fetch(getFullUrl(`${bp}/bp_machine_cat_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const remove_group_from_machine_category = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/remove_group_from_machine_category`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_supplier_data = async (supplierData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof supplierData === 'object' && !Array.isArray(supplierData)
      ? { ...supplierData, plant_id: plantId }
      : supplierData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_supplier_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const generate_parameter_pdf_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/generate_parameter_pdf_report`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const batch_summary_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/batch_summary_report`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_new_pidilite_pdf_report = async (batch_number, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_new_pidilite_pdf_report`, {
    params: {
      batch_number: batch_number,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_material_inentory_data = async (inventoryData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof inventoryData === 'object' && !Array.isArray(inventoryData)
      ? { ...inventoryData, plant_id: plantId }
      : inventoryData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_material_inventory`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const update_material_inventory = async (inventoryData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof inventoryData === 'object' && !Array.isArray(inventoryData)
      ? { ...inventoryData, plant_id: plantId }
      : inventoryData;

  const response = await fetch(getFullUrl(`${bp}/update_material_inventory`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_manual_qr_code_for_ingredent = async (qr_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof qr_data === 'object' && !Array.isArray(qr_data)
      ? { ...qr_data, plant_id: plantId }
      : qr_data;

  const response = await fetch(getFullUrl(`${bp}/save_manual_qr_code_for_ingredent`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const save_manual_qr_code_for_machine = async (qr_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof qr_data === 'object' && !Array.isArray(qr_data)
      ? { ...qr_data, plant_id: plantId }
      : qr_data;

  const response = await fetch(getFullUrl(`${bp}/save_manual_qr_code_for_machine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_machine_shift = async (shift_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof shift_data === 'object' && !Array.isArray(shift_data)
      ? { ...shift_data, plant_id: plantId }
      : shift_data;

  const response = await fetch(getFullUrl(`${bp}/add_machine_shift`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_unit_data = async (unitdata, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof unitdata === 'object' && !Array.isArray(unitdata)
      ? { ...unitdata, plant_id: plantId }
      : unitdata;

  const response = await fetch(getFullUrl(`${bp}/bp_add_unit`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_location_data = async (locationData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof locationData === 'object' && !Array.isArray(locationData)
      ? { ...locationData, plant_id: plantId }
      : locationData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_location`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_employee_shift = async (locationData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof locationData === 'object' && !Array.isArray(locationData)
      ? { ...locationData, plant_id: plantId }
      : locationData;

  const response = await fetch(getFullUrl(`${bp}/bp_save_employee_shift`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_machine_data = async (formData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && formData instanceof FormData) {
    formData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/bp_add_machine_data`), {
    method: 'POST',

    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  await handleResponse(response, success);
};

export const ImportIngredentQR = async (formData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && formData instanceof FormData) {
    formData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/ImportIngredentQR`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  await handleResponse(response, success);
};

export const Add_Bulk_Machine_Shift = async (formData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && formData instanceof FormData) {
    formData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/Add_Bulk_Machine_Shift`), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  await handleResponse(response, success);
};

export const get_recipe_meta_form = async (success, params) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_recipe_meta_form`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('get_recipe_meta_form error:', error));
};

export const edit_recipe_meta_data = async (metaData, success) => {
  const token = await localStorage.getItem('Token');
  const response = await fetch(getFullUrl(`${bp}/edit_recipe_meta_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(metaData),
  });
  await handleResponse(response, success);
};

export const bp_add_meta_data = async (metaData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof metaData === 'object' && !Array.isArray(metaData)
      ? { ...metaData, plant_id: plantId }
      : metaData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_recipe_meta_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_ingredient_data = async (ingredienetData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof ingredienetData === 'object' && !Array.isArray(ingredienetData)
      ? { ...ingredienetData, plant_id: plantId }
      : ingredienetData;

  const response = await fetch(getFullUrl(`${bp}/bp_recipe_ingredients_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_process_data = async (processData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof processData === 'object' && !Array.isArray(processData)
      ? { ...processData, plant_id: plantId }
      : processData;

  const response = await fetch(getFullUrl(`${bp}/bp_process_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_sample_actual_data = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const formData = new FormData();
  formData.append('sample_detail_id', data.sample_detail_id);
  formData.append('data_flow', JSON.stringify(data.data_flow || []));
  if (data.observation !== undefined) formData.append('observation', data.observation || '');
  if (data.sample_number) formData.append('sample_number', data.sample_number);
  if (data.quantity !== undefined && data.quantity !== null) formData.append('quantity', data.quantity);
  if (data.evidence_description) formData.append('evidence_description', data.evidence_description);
  if (data.min_value !== undefined && data.min_value !== null) formData.append('min_value', data.min_value);
  if (data.max_value !== undefined && data.max_value !== null) formData.append('max_value', data.max_value);
  if (data.evidence_files?.length) {
    data.evidence_files.forEach((file) => formData.append('evidence_files', file));
  }
  if (plantId) formData.append('plant_id', plantId);

  const response = await fetch(getFullUrl(`${bp}/bp_save_sample_actual_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
    body: formData,
  });
  await handleResponse(response, success);
};

export const bp_add_recipe_data = async (recipeData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof recipeData === 'object' && !Array.isArray(recipeData)
      ? { ...recipeData, plant_id: plantId }
      : recipeData;

  const response = await fetch(getFullUrl(`${bp}/bp_add_recipe_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_process_details = async (processData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof processData === 'object' && !Array.isArray(processData)
      ? { ...processData, plant_id: plantId }
      : processData;

  const response = await fetch(getFullUrl(`${bp}/bp_save_process_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_recipe_draft = async (processData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof processData === 'object' && !Array.isArray(processData)
      ? { ...processData, plant_id: plantId }
      : processData;

  const response = await fetch(getFullUrl(`${bp}/save_recipe_draft`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const delete_recipe_draft = async (processData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof processData === 'object' && !Array.isArray(processData)
      ? { ...processData, plant_id: plantId }
      : processData;

  const response = await fetch(getFullUrl(`${bp}/delete_recipe_draft`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_product_inv_data = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_all_process_data`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const getProcessByRecipeId = async (recipe_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_all_process_data`, {
    params: { recipe_id: recipe_id },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_save_sampling_config = async (samplingConfigData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof samplingConfigData === 'object' && !Array.isArray(samplingConfigData)
      ? { ...samplingConfigData, plant_id: plantId }
      : samplingConfigData;

  const response = await fetch(getFullUrl(`${bp}/bp_save_sampling_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Separate UPDATE API for Sample Config
export const update_sampling_config = async (samplingConfigData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof samplingConfigData === 'object' && !Array.isArray(samplingConfigData)
      ? { ...samplingConfigData, plant_id: plantId }
      : samplingConfigData;

  const response = await fetch(getFullUrl(`${bp}/update_sampling_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_add_testing_type = async (testing_type, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof testing_type === 'object' && !Array.isArray(testing_type)
      ? { ...testing_type, plant_id: plantId }
      : testing_type;

  const response = await fetch(getFullUrl(`${bp}/bp_add_testing_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Separate UPDATE API for Test Types
export const update_testing_type = async (testing_type, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof testing_type === 'object' && !Array.isArray(testing_type)
      ? { ...testing_type, plant_id: plantId }
      : testing_type;

  const response = await fetch(getFullUrl(`${bp}/update_testing_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_update_process_approval_status = async (approvalreq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof approvalreq === 'object' && !Array.isArray(approvalreq)
      ? { ...approvalreq, plant_id: plantId }
      : approvalreq;

  const response = await fetch(getFullUrl(`${bp}/bp_update_process_approval_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_operationType = async (opTypeReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof opTypeReq === 'object' && !Array.isArray(opTypeReq)
      ? { ...opTypeReq, plant_id: plantId }
      : opTypeReq;

  const response = await fetch(getFullUrl(`${bp}/bp_add_operation_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const department_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/departdata`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_sampling_config_details = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_sampling_config_details`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_operation_type = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_operation_type`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_all_testing_type = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/bp_get_all_testing_type`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_role = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/bp_get_role`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_process_by_schedule = async (batchNo, batchId, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_process_name_by_schedule`, {
    params: { batch_number: batchNo, batch_id: batchId },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const bp_get_pending_recipe = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_pending_recipe`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_prod_recipe_approval_list = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_prod_recipe_approval_list`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_approval_details = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_approval_details`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_approval_recipe = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/bp_get_approval_recipe`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_update_employee_for_batch_number = async (empreq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof empreq === 'object' && !Array.isArray(empreq)
      ? { ...empreq, plant_id: plantId }
      : empreq;

  const response = await fetch(getFullUrl(`${bp}/bp_update_employee_for_batch_number`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_sensor_status = async (senReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof senReq === 'object' && !Array.isArray(senReq)
      ? { ...senReq, plant_id: plantId }
      : senReq;

  const response = await fetch(getFullUrl(`${bp}/update_sensor_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_save_recipe_status = async (recipe_status_req, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof recipe_status_req === 'object' && !Array.isArray(recipe_status_req)
      ? { ...recipe_status_req, plant_id: plantId }
      : recipe_status_req;

  const response = await fetch(getFullUrl(`${bp}/bp_save_recipe_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const send_recipe_approval_status = async (recipe_status_req, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof recipe_status_req === 'object' && !Array.isArray(recipe_status_req)
      ? { ...recipe_status_req, plant_id: plantId }
      : recipe_status_req;

  const response = await fetch(getFullUrl(`${bp}/send_recipe_approval_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_reject_recipe_status = async (recipe_status_req, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof recipe_status_req === 'object' && !Array.isArray(recipe_status_req)
      ? { ...recipe_status_req, plant_id: plantId }
      : recipe_status_req;

  const response = await fetch(getFullUrl(`${bp}/bp_reject_recipe_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const bp_get_approval_status = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_approval_recipe`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_batch_notification_for_employee = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_batch_notification_for_employee`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_notification_for_recipe_approval_action = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_notification_for_recipe_approval_action`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ingredient_value_alert = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_ingredient_value_alert`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_recipe_approval = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_recipe_approval`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

//################# BKT #####################////

export const get_running_batch_details = async (filter, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_running_batch_details`, {
    params: { ...filter, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_schedule_batch_details = async (filter, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_schedule_batch_details`, {
    params: { ...filter, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_completed_batch_details = async (filter, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_completed_batch_details`, {
    params: { ...filter, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_report_pdf = async (batch_number, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_batch_report_pdf`, {
    params: { batch_number: batch_number },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const config_weighing_machine = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/config_weighing_machine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_machine_room = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_machine_room`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_machine_room = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const response = await fetch(`${bp}/update_machine_room`, {
    method: 'POST', // API expects POST
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(configReq), // sending the data
  });

  const text = await response.text();

  await handleResponse(response, success);
};

export const save_machine_category = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/save_machine_category`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const update_schedule_time = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_schedule_time`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_batch_details = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_batch_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const store_sample_detail_status = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/store_sample_detail_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const copy_recipe_data = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/copy_recipe_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const copy_batch_details = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/copy_batch_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const resampling_sample = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/resampling_sample`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const edit_operation = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/edit_operation`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const update_recipe_approval_status = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_recipe_approval_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_recipe = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_recipe`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_all_employee_for_process = async (success, configReq) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof success === 'object' && !Array.isArray(success)
      ? { ...success, plant_id: plantId }
      : success;

  const response = await fetch(getFullUrl(`${bp}/update_all_employee_for_process`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(configReq),
  });
  await handleResponse(response, success);
};

export const add_user_code = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_user_code`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_sensor_type = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_sensor_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_device_control_system = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_device_control_system`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_Tags = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_Tags`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_sub_machine = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/save_sub_machine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_sensor_type = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_sensor_type`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_machine_category = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_machine_category`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_room_name = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_room_name`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_user_code = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_user_code`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_device_control_system = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_device_control_system`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_sub_machine = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_sub_machine`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const bp_get_parameters = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/bp_get_parameters`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_bill_of_materials = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_bill_of_materials`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_final_recipe_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_all_recipe_details`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_weighing_machine_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_weighing_machine_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_grafana_url = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_grafana_url`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      // Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_operation_alert = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_operation_alert`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_approval = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_recipe_approval`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_approval_role_flow = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_approval_role_flow`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_copy_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_copy_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_get_all_process_data = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/bp_get_all_process_data`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_changes_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_batch_changes_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_material_balance = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_material_balance`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_details_by_recipe_id = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_recipe_details_by_recipe_id`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_draft_data = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_recipe_draft_data`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_parameter_tag_by_machine = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_parameter_tag_by_machine`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_printer_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_printer_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_weight_by_id = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_weight_by_id`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const show_termianted_batchs = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/show_termianted_batchs`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const show_bach_progress_flow = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/show_bach_progress_flow`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const generate_material_machine_print = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/generate_material_machine_print`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_machine_details = async (formData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to FormData if it exists
  if (plantId && formData instanceof FormData) {
    formData.append('plant_id', plantId);
  }

  const response = await fetch(getFullUrl(`${bp}/update_machine_details`), {
    method: 'POST',

    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  await handleResponse(response, success);
};

export const edit_equiptment_details = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/edit_equiptment_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_machine = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_machine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_material = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_material`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_product = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_product`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_product_data = async (productData, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/delete_product_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(productData),
  });
  await handleResponse(response, success);
};

export const update_delete_sensor = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_delete_sensor`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_delete_parameter = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_delete_parameter`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_all_process_data_for_operation = async (success, params, signal) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_all_process_data_for_operation`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((e) => {
      console.log(e);
    });
};

export const delete_equipment = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_equipment`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const complete_kitting_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/complete_kitting_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_qr_material_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_qr_material_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_archived_recipe_list = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_archived_recipe_list`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_all_recipe_by_product = async (success, params, onError) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_all_recipe_by_product`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
      // Let the caller react (e.g. stop the spinner and show "No Data").
      if (typeof onError === 'function') onError(error);
    });
};

// Field-level edit history / saved logs for a recipe (django-auditlog change history).
export const get_recipe_change_history = async (params, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/change-history/recipe/`, {
    params: { ...params },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Error fetching recipe change history:', error);
      success({ data: { status: 'failed', data: [] } });
    });
};

export const terminate_batch_details = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/terminate_batch_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const reprint_child_qr = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/reprint_child_qr`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};
export const archive_recipe = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/archive_recipe`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const setting_configuration = async (settingData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof settingData === 'object' && !Array.isArray(settingData)
      ? { ...settingData, plant_id: plantId }
      : settingData;

  const response = await fetch(getFullUrl(`${bp}/setting_configuration`), {
    method: 'POST',

    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_list_of_setting = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_list_of_setting`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_batch_process_step = async (batch_number, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_batch_process_steps?batch_number=${batch_number}`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_Machine_Available = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_machine_availability_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_standard_checklist = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_standard_checklist`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_Next_Ticket = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_next_ticket_stage`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ticket_Stage_checklist = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ticketstage_checklist`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ticket_timeline = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ticket_timeline`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const add_workflow_flow = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_workflow_flow`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const Submit_Standard_Checklist = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_ticket_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_issue_type_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(
    `bp/get_issue_type_list
 `,
    {
      params: plantId ? { plant_id: plantId } : {},

      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  ).then((response) => {
    success(response);
  });
};

export const get_ticket_status = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ticket_status`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const add_ticket_status = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/add_ticket_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const delete_ticket_details = async (ticket_id) => {
  try {
    const token = await localStorage.getItem('Token');
    const plantId = getActivePlantId();
    const response = await fetch(getFullUrl(`${bp}/delete_ticket_details/${ticket_id}`), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ticket: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    console.error('Error deleting ticket:', error);
    throw error;
  }
};

export const get_workflow_flow = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_workflow_flow`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_check_batch_clearance = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/check_batch_all_condition`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_all_batch_machine_cleaning_operation = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_all_batch_machine_cleaning_operation`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_form_builder_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_all_forms`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_product_master_form_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_additional_field/?key=prod_m`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const post_formadata_additional_fields = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(`${bp}/manage_additional_field`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(configReq),
  });
  await handleResponse(response, success);
};

export const get_pending_package = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_pending_package`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_for_schedule = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_recipe_for_schedule`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_summary_dashboard = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_summary_dashboard_for_batch`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_standard_blocks = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_standard_blocks`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_gantt_chart = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/gantt_chart_view`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const bp_refilling_logs = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_bp_refilling_logs`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_completed_batch_details_for_refill`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const create_standard_block = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/create_standard_block`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_standard_block = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_standard_block`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_cleaning_status = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_cleaning_status`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const webSocketRunning = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  // Construct query parameters string
  const queryString = new URLSearchParams(params).toString();
  console.log(queryString);

  // Create WebSocket connection
  const socket = new WebSocket(`ws://localhost:8000/ws/batch/${queryString}/`);

  socket.onopen = () => {
    console.log('WebSocket connected');

    // Optional: send token or auth message if backend expects it via WebSocket
    socket.send(JSON.stringify({ type: 'auth', token }));
  };
  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    success(data); // Send data to success callback
    console.log('WebSocket connected: ', data);
  };
  socket.onerror = (error) => {
    console.error('WebSocket error', error);
  };
  socket.onclose = () => {
    console.log('WebSocket connection closed');
  };
  // Optional: return socket so caller can manually close it if needed
  return socket;
};

// Pharma

export const create_warehouse_area_config = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/werehouse_area_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const update_warehouse_area_config = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/update_warehouse_area_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const process_scheduling_config = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/process_scheduling_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const dispensing_scheduling_config = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/dispensing_scheduling_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const assign_warehouse_area_for_dispensing = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/assign_warehouse_area_for_dispensing`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const assign_warehouse_area_process = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/assign_warehouse_area_process`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// GET /bp/get_werehouse_area
export const get_warehouse_area = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_werehouse_area`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

// GET /bp/get_werehouse_area
export const get_area_officer_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_area_officer_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_dispense_batches_status = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_dispense_batches_status`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_dispen_and_process_details = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_dispen_and_process_details`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_cleaning_type = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_cleaning_type`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_dispensing_line_clearance = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_dispensing_line_clearance`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_ingredient_for_batch = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ingredient_for_batch`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_api_calculation_config = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_api_calculation_config`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_current_shift_employee_list = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_current_shift_employee_list`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_schedule_processes = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_schedule_processes`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_process_stages_status = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_process_stages_status`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_process_line_clearance = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_process_line_clearance`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_process_operation = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_process_operation`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const save_dispensing_line_clearance = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/save_dispensing_line_clearance`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const give_approval_for_dispens_line_clearance = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/give_approval_for_dispens_line_clearance`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const api_calculation_config = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/api_calculation_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const approve_api_calculations = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/approve_api_calculations`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const assign_operator_for_dispensing = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/assign_operator_for_dispensing`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const assign_operator_for_process_operation = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/assign_operator_for_process_operation`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const complete_dispensing = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/complete_dispensing`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const complete_process = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/complete_process`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const save_process_line_clearance = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/save_process_line_clearance`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const give_approval_for_process_line_clearance = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/give_approval_for_process_line_clearance`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const esign_verification_and_log = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/esign_verification_and_log`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const get_mbmr_dashboard = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_mbmr_dashboard`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_ebmr_dashboard = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_ebmr_dashboard`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_machine_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_machine_list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_completed_batches = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_completed_batches`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_combined_audit_logs = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_combined_audit_logs`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_master_batchs = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_master_batchs`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_parent_lot_batch_details = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_parent_lot_batch_details`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_batch_report_pdf_for_multiple_lots = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_batch_report_pdf_for_multiple_lots`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_complete_dispense_batches_material = async (param, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_complete_dispense_batches_material`, {
    params: { ...param, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      success(response);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};
export const get_all_stage_names = async (param, success, failure) => {
  try {
    const token = localStorage.getItem('Token');
    const response = await Axios.get(`${bp}/get_all_stage_names`, {
      params: param,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    success(response);
  } catch (error) {
    if (failure) failure(error);
    console.error('Error in get_all_stage_names:', error);
  }
};

export const bmr_overall_pdf_report = async (param, success, failure) => {
  try {
    const token = localStorage.getItem('Token');
    const response = await Axios.get(`${bp}/bmr_overall_pdf_report`, {
      params: param,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    success(response);
  } catch (error) {
    if (failure) failure(error);
    console.error('Error in bmr_overall_pdf_report:', error);
  }
};

export const batch_configuration_combine = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/batch_configuration_combine`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const get_parent_batch_details = async (success) => {
  try {
    const token = await localStorage.getItem('Token');
    const plantId = getActivePlantId();
    Axios.get(`${bp}/get_parent_batch_details`, {
      params: plantId ? { plant_id: plantId } : {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        success(response);
      })
      .catch((error) => {
        console.error('There was an error!', error);
      });
  } catch (error) {
    console.log(error);
  }
};

export const get_recipe_process_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_recipe_process_details`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const add_clearence_type = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/add_clearence_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

// Separate UPDATE API for Clearance Types
export const update_clearence_type = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/update_clearence_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  await handleResponse(response, success);
};

export const get_clearence_type = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_clearence_type`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_calculation_field = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_calculation_field`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const add_calculation_field = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/add_calculation_field`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Separate UPDATE API for API Calculation Field
export const update_calculation_field = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/update_calculation_field`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const save_cleaning_type_for_line = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/save_cleaning_type_for_line`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Separate UPDATE API for Cleaning Types
export const update_cleaning_type = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/update_cleaning_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_cleaning_type_for_line = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_cleaning_type_for_line`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_zydus_batch_report_pdf = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_zydus_batch_report_pdf`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const mbmr_overall_pdf_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/mbmr_overall_pdf_report`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const export_recipe_json = async (recipe_id, success, errorCb) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/export_recipe`, {
    params: { recipe_id, ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('export_recipe_json error', error);
      if (errorCb) errorCb(error);
    });
};

export const import_recipe_json = async (payload, success, errorCb) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.post(`${bp}/import_recipe`, { ...payload, ...(plantId && { plant_id: plantId }) }, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('import_recipe_json error', error);
      if (errorCb) errorCb(error);
    });
};

export const get_release_approval_details = async (filter, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_release_approval_details`, {
    params: { ...filter, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const release_approval = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/release_approval`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const add_actionlist_data = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/add_actionlist_data`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const store_machine_dsc_details = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, plant_id: plantId }
      : data;

  const response = await fetch(getFullUrl(`${bp}/store_machine_dsc_details`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_actionlist_data = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_actionlist_data`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_sub_operation_list = (successRes) => {
  get_function(`${bp}/get_sub_operation_list`, (res) => {
    successRes(res);
  });
};

export const getSubOpMappingList = (successRes) => {
  get_function(`${bp}/getSubOpMappingList`, (res) => {
    successRes(res);
  });
};

export const assign_sub_operations_to_operation_type = (successRes, reqData = {}) => {
  post_function(
    `${bp}/assign_sub_operations_to_operation_type`,
    (res) => {
      successRes(res);
    },
    reqData
  );
};

export const update_sub_operation_to_optype = (successRes, reqData) => {
  post_function(
    `${bp}/update_sub_operations_by_operation_type`,
    (res) => {
      if (res?.status === 200 || res?.data?.status === 'success') {
        successRes(res);
      } else {
        console.log('Update failed:', res);
        toast.error(res?.data?.message || 'Something went wrong!');
      }
    },
    reqData // body data to send
  );
};

export const update_actionlist_data = (successRes, reqData) => {
  post_function(
    `${bp}/update_actionlist_data`,
    (res) => {
      if (res?.status === 200 || res?.data?.status === 'success') {
        successRes(res);
      } else {
        console.log('Update failed:', res);
        toast.error(res?.data?.message || 'Something went wrong!');
      }
    },
    reqData // body data to send
  );
};

export const get_zydus_single_batch_report_pdf = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_zydus_single_batch_report_pdf`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_final_batch_pdf_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_final_batch_pdf_report`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const generate_bpr_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/generate_bpr_report`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_running_batch_pdf_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_running_batch_pdf_report`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const generate_batch_parameter_report = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/generate_batch_parameter_report`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipes_by_product = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_recipes_by_product`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_process_approval_dashboard = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_process_approval_dashboard`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_recipe_approval_dashboard = async (success, params) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_recipe_approval_dashboard`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_prod_recipe_approval_dashboard = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_prod_recipe_approval_dashboard`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_machines_with_tags_or_registers = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_machines_with_tags_or_registers`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const get_security_events = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/audit/security-events/`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching security events:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

export const get_master_data_changes = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/audit/master-data-changes/`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching master data changes:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

export const get_data_integrity_logs = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/audit/data-integrity/`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching data integrity logs:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

export const get_permission_changes = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/audit/permission-changes/`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching permission changes:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

export const get_audit_reviews = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/audit/reviews/`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching audit reviews:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

export const get_recipe_approval_by_recipe_id = async (recipeId, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_recipe_approval_by_recipe_id?recipe_id=${recipeId}`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('Error fetching recipe approval workflow:', error);
      if (success) {
        success({ data: { status: 'failed', message: error.message } });
      }
    });
};

/**
 * Get Batch Calendar Data
 * Fetches all running, upcoming, and completed batches with schedule information
 * For pharma batch calendar dashboard
 */
export const get_batch_calendar_data = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_batch_calendar_data`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: {
            running_batches: [],
            upcoming_batches: [],
            completed_batches: [],
          },
          status: 'failed',
          message: 'Failed to fetch batch calendar data',
        },
      };
      success(res);
      console.error('Error fetching batch calendar data:', error);
    });
};

// ==================== CLEANING LOGS APIs ====================

// GET: Get list of completed batches for cleaning filter dropdown
export const get_completed_batches_for_cleaning = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/cleaning_logs/batches/completed`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: [],
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch completed batches',
        },
      };
      success(res);
      console.error('Error fetching completed batches for cleaning:', error);
    });
};

// GET: Get list of equipment for cleaning filter dropdown
export const get_equipment_list_for_cleaning = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/cleaning_logs/equipment/list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: [],
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch equipment list',
        },
      };
      success(res);
      console.error('Error fetching equipment list for cleaning:', error);
    });
};

// GET: Get cleaning logs filtered by batch number
export const get_cleaning_logs_by_batch = async (batch_number, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/cleaning_logs/filter/batch/${batch_number}`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: {
            batch_info: null,
            cleaning_activities: [],
          },
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch cleaning logs',
        },
      };
      success(res);
      console.error('Error fetching cleaning logs by batch:', error);
    });
};

// GET: Get cleaning logs filtered by equipment/machine ID
export const get_cleaning_logs_by_equipment = async (equipment_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/cleaning_logs/filter/equipment/${equipment_id}`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: {
            equipment_info: null,
            cleaning_activities: [],
          },
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch cleaning logs',
        },
      };
      success(res);
      console.error('Error fetching cleaning logs by equipment:', error);
    });
};

// GET: Get detailed information for a specific cleaning log
export const start_machine_cleaning = async (cleaning_log_id, data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/cleaning_logs/${cleaning_log_id}/start`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

// POST: Complete cleaning process for a machine
export const complete_machine_cleaning = async (cleaning_log_id, data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/cleaning_logs/${cleaning_log_id}/complete`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

// POST: Approve or reject cleaning (QA/Admin only)
export const approve_machine_cleaning = async (cleaning_log_id, data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/cleaning_logs/${cleaning_log_id}/approve`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

// GET: Export cleaning logs (placeholder - will return PDF/Excel)
export const export_cleaning_logs = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/cleaning_logs/export`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to export cleaning logs',
        },
      };
      success(res);
      console.error('Error exporting cleaning logs:', error);
    });
};

// ==================== BATCH E-BOOK APIs ====================

// GET: Get batch list for e-book dropdown
export const get_batch_ebook_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/batch_ebook/batches/list`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          data: [],
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch batch list',
        },
      };
      success(res);
      console.error('Error fetching batch list for e-book:', error);
    });
};

// GET: Get complete batch e-book with hierarchical structure
export const get_batch_ebook = async (batch_number, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/batch_ebook/${batch_number}`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      let res = {
        data: {
          status: 'failed',
          message: error?.response?.data?.message || 'Failed to fetch batch e-book',
        },
      };
      success(res);
      console.error('Error fetching batch e-book:', error);
    });
};

// ==================== LMS Integration APIs ====================

// Get LMS configurations
export const get_lms_configurations = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_lms_configurations`, {
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res.data);
    })
    .catch((error) => {
      console.error('Error fetching LMS configurations:', error);
      let res = {
        status: 'failed',
        message: error?.response?.data?.message || 'Failed to fetch LMS configurations',
        data: [],
      };
      success(res);
    });
};

// Add LMS configuration
export const add_lms_configuration = async (lms_data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof lms_data === 'object' && !Array.isArray(lms_data)
      ? { ...lms_data, plant_id: plantId }
      : lms_data;

  const response = await fetch(getFullUrl(`${bp}/add_lms_configuration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Update LMS configuration
export const delete_lms_configuration = async (config_id, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof config_id === 'object' && !Array.isArray(config_id)
      ? { ...config_id, plant_id: plantId }
      : config_id;

  const response = await fetch(getFullUrl(`${bp}/delete_lms_configuration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ config_id }),
  });
  await handleResponse(response, success);
};

export const get_equipment_logs = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  // Convert params object to query string
  const query = new URLSearchParams(params)?.toString();
  const response = await fetch(getFullUrl(`${bp}/equipment_logs/history?${query}`), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  await handleResponse(response, success);
};

// ==================== Plant Configuration APIs ====================
export const get_all_plants_list = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/GetAllPlantsListView`), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  const data = await response.json();
  await handleResponse(response, success);
};

export const create_plant_configuration = async (plantData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof plantData === 'object' && !Array.isArray(plantData)
      ? { ...plantData, plant_id: plantId }
      : plantData;

  const response = await fetch(getFullUrl(`${bp}/PlantConfigurationView`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const data = await response.json();
  success(data);
};

export const update_plant_configuration = async (plantData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof plantData === 'object' && !Array.isArray(plantData)
      ? { ...plantData, plant_id: plantId }
      : plantData;

  const response = await fetch(getFullUrl(`${bp}/UpdatePlantConfiguration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const data = await response.json();
  success(data);
};

export const delete_plant_configuration = async (plantData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  // Add plant_id to data if it exists and data is an object
  const requestData =
    plantId && typeof plantData === 'object' && !Array.isArray(plantData)
      ? { ...plantData, plant_id: plantId }
      : plantData;

  const response = await fetch(getFullUrl(`${bp}/DeletePlantConfiguration`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const data = await response.json();
  success(data);
};

export const get_register_machine = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/get_register_machine`), {
    method: 'GET',
    params: plantId ? { plant_id: plantId } : {},
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  const data = await response.json();
  await handleResponse(response, success);
};

export const get_register_by_machine = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  const response = await fetch(getFullUrl(`${bp}/get_register_by_machine`), {
    method: 'GET',
    params: { ...params, ...(plantId ? { plant_id: plantId } : {}) },
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  const data = await response.json();
  await handleResponse(response, success);
};

// ==================== Configuration Pages Delete APIs ====================

// 1. Delete Operation Type Configuration
export const delete_operation_type = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_operation_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 2. Delete Room Configuration
export const delete_room_config = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_room_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 3. Delete Area Configuration
export const delete_area_config = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_area_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 4. Delete Sensor Configuration
export const delete_cleaning_type = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_cleaning_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 6. Delete Test Type Configuration
export const delete_test_type = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_test_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 7. Delete API Calculation Field
export const delete_api_calc_field = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_api_calc_field`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// 8. Delete Clearance Type Configuration
export const delete_clearance_type = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_clearance_type`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Process Control
export const get_process_control = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_process_control`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

// Transfer Path Config APIs
export const add_transfer_path_config = async (configData, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configData === 'object' && !Array.isArray(configData)
      ? { ...configData, plant_id: plantId }
      : configData;

  const response = await fetch(getFullUrl(`${bp}/add_transfer_path_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

export const get_transfer_path_config = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_transfer_path_config`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      success(res);
    })
    .catch((error) => {
      console.error('There was an error!', error);
    });
};

export const update_delete_transfer_path_config = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_delete_transfer_path_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    success(parsed);
  } catch {
    console.error('update_delete_transfer_path_config failed:', response.status, text);
    success({ status: 'failed', message: text || 'Request failed' });
  }
};

export const get_transfer_path_rules = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_transfer_path_rules`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const get_transfer_path_rule_assignments = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_transfer_path_rule_assignments`, {
    params: {
      ...params,
      ...(plantId && { plant_id: plantId }),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const upsert_transfer_path_rule_assignment = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/upsert_transfer_path_rule_assignment`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    success(parsed);
  } catch {
    console.error('upsert_transfer_path_rule_assignment failed:', response.status, text);
    success({ status: 'failed', message: text || 'Request failed' });
  }
};

export const update_delete_transfer_path_rule_assignment = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/update_delete_transfer_path_rule_assignment`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    success(parsed);
  } catch {
    console.error('update_delete_transfer_path_rule_assignment failed:', response.status, text);
    success({ status: 'failed', message: text || 'Request failed' });
  }
};

// 9. Delete Sample Configuration
export const delete_sample_config = async (configReq, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const requestData =
    plantId && typeof configReq === 'object' && !Array.isArray(configReq)
      ? { ...configReq, plant_id: plantId }
      : configReq;

  const response = await fetch(getFullUrl(`${bp}/delete_sample_config`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  await handleResponse(response, success);
};

// Control Category APIs
export const get_control_category = async (success, id = null) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_control_category`, {
    params: { ...(plantId && { plant_id: plantId }), ...(id && { id }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const get_control_category_with_sub_categories = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_control_category_with_sub_categories`, {
    params: { ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const add_control_category = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const response = await fetch(getFullUrl(`${bp}/add_control_category`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...data, ...(plantId && { plant_id: plantId }) }),
  });
  await handleResponse(response, success);
};

export const update_delete_control_category = async (data, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/update_delete_control_category`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

export const get_sub_control_category = async (success, id = null) => {
  const token = await localStorage.getItem('Token');

  Axios.get(`${bp}/get_sub_control_category`, {
    params: { ...(id && { id }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const assign_sub_control_category = async (data, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/assign_sub_control_category`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

export const delete_sub_category_from_category = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const response = await fetch(getFullUrl(`${bp}/delete_sub_category_from_category`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

export const get_process_control_parameter = async (success, id = null) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  Axios.get(`${bp}/get_process_control_parameter`, {
    params: { ...(plantId && { plant_id: plantId }), ...(id && { id }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const add_process_control_parameter = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();

  const response = await fetch(getFullUrl(`${bp}/add_process_control_parameter`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...data, ...(plantId && { plant_id: plantId }) }),
  });
  await handleResponse(response, success);
};

export const update_process_control_parameter = async (data, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/update_process_control_parameter`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

export const delete_process_control_parameter = async (id, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/delete_process_control_parameter`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  });
  await handleResponse(response, success);
};

export const update_delete_process_control_parameter = async (data, success) => {
  const token = await localStorage.getItem('Token');

  const response = await fetch(getFullUrl(`${bp}/update_delete_process_control_parameter`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};

export const get_parameters_for_transfer_path = async (success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_parameters_for_transfer_path`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then((res) => {
    success(res);
  });
};

export const get_process_control_valves = async (success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_process_control_valves`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const check_parameter_format = async (success, id_format) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/check_parameter_format`, {
    params: { id_format },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const get_outbound_events = async (success, params = {}) => {
  const token = await localStorage.getItem('Token');
  Axios.get('/api/get-outbound-events/', {
    params,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const get_machine_group = async (success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_machine_group`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('There was an error!', error));
};

export const add_machine_group = async (data, success) => {
  const token = await localStorage.getItem('Token');
  const response = await fetch(getFullUrl(`${bp}/add_machine_group`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  await handleResponse(response, success);
};


export const get_recipe_processes_with_operations = async (params, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_recipe_processes_with_operations`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('Error fetching recipe processes:', error));
};

export const get_operation_operation_type = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_operation_operation_type`, {
    // _ts cache-buster + no-cache headers so the DAG always reflects the latest
    // edits (machine groups on edges) instead of a browser-cached response.
    params: { ...params, ...(plantId && { plant_id: plantId }), _ts: Date.now() },
    headers: {
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
    .then((res) => success(res))
    .catch((error) => console.error('Error fetching operation type:', error));
};

// Sync materials from ERP system
export const sync_material_from_erp = async (success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.post(`${bp}/sync_material_from_erp`, plantId ? { plant_id: plantId } : {}, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Error syncing materials from ERP:', error);
      success({ data: { status: 'failed', message: 'ERP sync failed' } });
    });
};

// Fetch available lots for a given material (for BOM lot allocation in Schedule Batch)
export const get_material_lots = async (params, success) => {
  const token = await localStorage.getItem('Token');
  const plantId = getActivePlantId();
  Axios.get(`${bp}/get_material_lots`, {
    params: { ...params, ...(plantId && { plant_id: plantId }) },
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => {
      console.error('Error fetching material lots:', error);
      success({ data: { status: 'failed', data: [] } });
    });
};

export const get_seperate_qr_details = async (params, success) => {
  const token = await localStorage.getItem('Token');
  Axios.get(`${bp}/get_seperate_qr_details`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => success(res))
    .catch((error) => console.error('get_seperate_qr_details error', error));
};

export const reprint_seperate_qr = async (payload, success) => {
  const token = await localStorage.getItem('Token');
  Axios.post(`${bp}/reprint_seperate_qr`, payload, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
    .then((res) => success(res))
    .catch((error) => console.error('reprint_seperate_qr error', error));
};
